import React from "react";
import NavbarProgram from "../../components/NavbarProgram";
import { Helmet } from "react-helmet";
import { programData, ProgramType } from "../../data/programData";
import { handleCheckout } from "../../utils/checkout";
import PaymentSection from "../../components/paymentSection";
import ServiceProcess, { ServiceStep } from "../../components/serviceProcess";
import {
  AiOutlineCreditCard,
  AiOutlineForm,
  AiOutlineUser,
  AiOutlineFileText,
} from "react-icons/ai";
import TestimonialCard from "../../components/TestimonialCard";

const features = [
  {
    title: "UK‑Standard Format",
    description:
      "Receive a polished, recruiter‑friendly template designed for UK companies and applicant tracking systems (ATS).",
  },
  {
    title: "Customisable Source File",
    description:
      "Editable source file allows you to tailor your CV version for different roles while keeping structure consistent.",
  },
  {
    title: "Instant Download",
    description:
      "Get access immediately after form submission – you will receive an email within 24 hours.",
  },
  {
    title: "Design & Clarity Focus",
    description:
      "Clear typography, appropriate spacing, and visual hierarchy that aligns with UK job market expectations.",
  },
];

const processSteps: ServiceStep[] = [
  {
    id: 1,
    title: "Payment",
    description: "Complete the payment to get started.",
    icon: <AiOutlineCreditCard className="text-accent w-6 h-6" />,
    tip: "",
  },
  {
    id: 2,
    title: "Download File",
    description:
      "We'll send you the template file and instructions via email within 24 hours.",
    icon: <AiOutlineForm className="text-accent w-6 h-6" />,
    tip: "",
  },
  {
    id: 3,
    title: "Customize & Use",
    description:
      "Edit the template with your own details and tailor it for each application you send.",
    icon: <AiOutlineUser className="text-accent w-6 h-6" />,
    tip: "",
  },
  {
    id: 4,
    title: "Apply & Succeed",
    description:
      "Use your new CV format confidently to apply for UK graduate roles and internships.",
    icon: <AiOutlineFileText className="text-accent w-6 h-6" />,
    tip: "",
  },
];

// TBD - replace this with testimonialList.tsx have a program tag for this
const sampleTestimonial_1 = {
  header: "UK-style CV가 이렇게 다른지 처음 알았어요",
  content: `<p>
      한국에서 디자이너로 6년 정도 일하면서 커리어에는 어느 정도 자신이 있었지만,
      영국식 CV는 완전히 다른 접근이 필요하다는 걸 이번에 처음 알게 됐어요.
    </p>
    <p>
      BeMe의 Resume Template은 단순한 양식이 아니라,
      어떤 경험을 어떻게 정리해야 하는지까지 자연스럽게 이해할 수 있게 도와줬습니다.
    </p>
    <p>
      <strong>가격 부담 없이 고급 퀄리티의 포맷</strong>으로,
      잘 모르던 영국 취업을 훨씬 수월하게 시작할 수 있었어요.
    </p>`,
  star: 5,
  isHTML: true, 
  experience: "Senior Designer (6 yrs), South Korea",
};

const sampleTestimonial_2 = {
  header: "CV의 기준이 생겼다는 게 가장 큰 수확이었어요",
  content: `
    <p>
      어떤 형식이 경쟁력 있는지, 영국에서는 무엇을 중요하게 보는지 몰라서
      CV 작성이 막막하게 느껴졌습니다.
    </p>
    <p>
      BeMe Sample Resume를 구매하면서,
      단순한 디자인이 아니라 <strong>영국식 CV의 기준과 구조</strong>를
      명확히 이해할 수 있었어요.
    </p>
    <p>
      혼자였다면 계속 헤맸을 것 같은데,
      좋은 가이드라인이 생겨서 이후 지원 준비에 큰 도움이 되었습니다.
    </p>
      `
  , star: 5,
  isHTML: true, 
  experience: "Undergraduate Student, King’s College London",
};

const sampleTestimonial_3 = {
  header: "30개 이상 지원했지만, 계속 서류 탈락이 이어졌어요.",
  content:
    `
      <p>
      영국 현지에서 이직을 준비하면서 연이은 탈락으로
      방향성을 완전히 잃은 상태였습니다.
    </p>
    <br/>
    <p>
      그때 BeMe의 Resume Template을 알게 되었고,
      <strong>합리적인 가격에 퀄리티 높은 템플릿</strong>이라는 점에서
      큰 확신을 얻을 수 있었어요.
    </p>
    <p>
      이후에도 BeMe와 좋은 커넥션을 유지하며 많은 도움을 받고 있고,
      전반적인 취업 준비 과정에 매우 만족하고 있습니다.
    </p>
      `
  ,
  isHTML: true, 
  star: 5,
  experience: "Marketing (3 yrs), London",
};

const ResumeTemplatePage: React.FC = () => {
  const program = programData.find((p) => p.id === ProgramType.ResumeTemplate)!;

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>BeMe – Resume Template Download</title>
        <meta
          name="description"
          content="Download a UK‑style resume template from BeMe ideal for international students entering the UK job market."
        />
      </Helmet>

      <NavbarProgram />

      {/* Hero */}
      <section className="bg-main text-white py-20 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">Resume Template</h1>
        <p className="max-w-2xl mx-auto text-lg md:text-xl text-sub/90 mb-8">
          Get your recruiter‑ready UK‑style resume template and use it for your job applications
          today.
        </p>
        <button
          onClick={() => handleCheckout("resume-kit")}
          className="bg-accent text-white px-8 py-3 rounded-lg font-semibold shadow-md hover:bg-yellow-600 transition-colors"
        >
          Get Template
        </button>
      </section>

      {/* Features */}
      <section className="py-16 bg-sub">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-main text-center mb-12">Why This Template?</h2>
          <div className="grid md:grid-cols-2 gap-10">
            {features.map((f, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded shadow-md hover:shadow-lg transition-shadow"
              >
                <h3 className="text-xl font-semibold text-main mb-2">{f.title}</h3>
                <p className="text-gray-700">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <section className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-main mb-8 text-center">
            Success Stories
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <TestimonialCard testimonial={sampleTestimonial_1} />
            <TestimonialCard testimonial={sampleTestimonial_2} />
            <TestimonialCard testimonial={sampleTestimonial_3} />
          </div>
        </section>
      </section>

      {/* Process */}
      <section className="bg-sub py-20 px-4 text-center">
        <ServiceProcess label={program.label} steps={processSteps} />
      </section>

      <section className="bg-white py-20 px-4 text-center">
        <PaymentSection program={program} />
      </section>
    </div>
  );
};

export default ResumeTemplatePage;
